import React, { useMemo } from 'react';

import { formatMoney, toMoney } from '@betterment/core';
import { Box, Text } from '@betterment/samba-react';
import { SambaAutoLink } from '@betterment/shared-components';

import { DeferralSetting } from '../../graphql/schema';
import { urlBuilder } from '../../Shared/urlBuilder';

interface Props {
  deferralSettings: DeferralSetting[];
}

const enum deferralSettingStates {
  NOT_SET,
  SINGLE_DEFERRAL_SETTING,
  MULTIPLE_DEFERRAL_SETTINGS_WITH_SAME_TYPE,
  MULTIPLE_MIXED_DEFERRAL_SETTINGS,
}

const ContributionsCTA: React.FC<React.PropsWithChildren<Props>> = ({ deferralSettings }) => {
  const deferralsWithRates = useMemo(() => {
    return deferralSettings.filter(deferralSetting => {
      return (
        (deferralSetting.value.__typename === 'PayrollContributionConfigValuePercent' &&
          deferralSetting.value.percent > 0) ||
        (deferralSetting.value.__typename === 'PayrollContributionConfigValueAmount' &&
          deferralSetting.value.amount.cents > 0)
      );
    });
  }, [deferralSettings]);

  const hasNoDeferralSetting = () => {
    return deferralsWithRates.length === 0;
  };

  const hasSingleDeferralSetting = () => {
    return deferralsWithRates.length === 1;
  };

  const hasMultipleDeferralSettings = () => {
    return deferralsWithRates.length > 1;
  };

  const hasMixedDeferralSettingRateTypes = () => {
    const deferralSettingsTypes = new Set();
    deferralsWithRates.forEach(deferral => deferralSettingsTypes.add(deferral.value.__typename));
    return deferralSettingsTypes.size > 1;
  };

  const displayDeferralRate = useMemo(() => {
    if (deferralsWithRates.length > 0) {
      const totalRate = deferralsWithRates.reduce((partialRate, deferralSetting) => {
        if (deferralSetting.value.__typename === 'PayrollContributionConfigValuePercent') {
          return partialRate + deferralSetting.value.percent;
        }
        return partialRate + deferralSetting.value.amount.cents;
      }, 0);
      if (deferralsWithRates[0].value.__typename === 'PayrollContributionConfigValuePercent') {
        return `${totalRate}%`;
      }
      return formatMoney(toMoney(totalRate));
    }
    return null;
  }, [deferralsWithRates]);

  let deferralSettingState;

  if (hasNoDeferralSetting()) {
    deferralSettingState = deferralSettingStates.NOT_SET;
  } else if (hasSingleDeferralSetting()) {
    deferralSettingState = deferralSettingStates.SINGLE_DEFERRAL_SETTING;
  } else if (hasMultipleDeferralSettings() && hasMixedDeferralSettingRateTypes()) {
    deferralSettingState = deferralSettingStates.MULTIPLE_MIXED_DEFERRAL_SETTINGS;
  } else if (hasMultipleDeferralSettings() && !hasMixedDeferralSettingRateTypes()) {
    deferralSettingState = deferralSettingStates.MULTIPLE_DEFERRAL_SETTINGS_WITH_SAME_TYPE;
  }

  return (
    <Box data-testid="deferralSettingsCTA">
      {deferralSettingState === deferralSettingStates.NOT_SET && (
        <Text variant="text50">
          <SambaAutoLink to={urlBuilder.appFourOhOneKSelect.absolute()}>Set your contribution rate</SambaAutoLink>
        </Text>
      )}
      {deferralSettingState === deferralSettingStates.SINGLE_DEFERRAL_SETTING && (
        <Text color="text.weak" variant="text50">
          Based on your {displayDeferralRate} contribution rate |{' '}
          <SambaAutoLink
            to={urlBuilder.appDeferralSettings.absolute({
              employerId: String(deferralsWithRates[0].employer.legacyId),
            })}
          >
            Edit
          </SambaAutoLink>
        </Text>
      )}
      {deferralSettingState === deferralSettingStates.MULTIPLE_DEFERRAL_SETTINGS_WITH_SAME_TYPE && (
        <Text color="text.weak" variant="text50">
          Based on your {displayDeferralRate} contribution rate |{' '}
          <SambaAutoLink to={urlBuilder.appFourOhOneKSelect.absolute()}>Edit</SambaAutoLink>
        </Text>
      )}
      {deferralSettingState === deferralSettingStates.MULTIPLE_MIXED_DEFERRAL_SETTINGS && (
        <Text color="text.weak" variant="text50">
          Based on your contribution rates |{' '}
          <SambaAutoLink to={urlBuilder.appFourOhOneKSelect.absolute()}>Edit</SambaAutoLink>
        </Text>
      )}
    </Box>
  );
};

export default ContributionsCTA;
